/**
 * Booking data access.
 *
 * Replaces `fetchBookingsFromDB`, the two live-poll intervals in `loginPatient`
 * and the ad-hoc `window.bookings` mutations scattered through the dashboards.
 *
 * Note on the endpoint choice: the legacy code loaded the global booking list
 * from `/sync/bookings`, but that route only returns *today's* bookings for one
 * doctor. Anything that needed history was therefore silently empty. These hooks
 * use `/bookings` with an explicit filter, which is the route that actually
 * answers the question being asked.
 */

import { useCallback, useMemo, useState } from 'react';
import { bookingApi } from '../api/endpoints';
import { useAsync } from './useAsync';
import { usePoll } from './usePolling';
import { groupBookingsByBucket, compareBookingsChronologically } from '../domain/bookings';

/** How often the live views re-check the server, in milliseconds. */
export const POLL_INTERVAL = { PATIENT: 15000, DOCTOR: 10000, ADMIN: 30000 };

/**
 * Every booking for one patient, kept fresh by polling.
 *
 * @param {number|string|null} patientId
 * @param {{poll?: boolean, intervalMs?: number}} [options]
 */
export function usePatientBookings(patientId, options = {}) {
  const { poll = true, intervalMs = POLL_INTERVAL.PATIENT } = options;

  const state = useAsync(
    () => bookingApi.list({ patientId }),
    [patientId],
    { initialData: [], enabled: Boolean(patientId) },
  );

  usePoll(() => bookingApi.listFresh({ patientId }), poll && patientId ? intervalMs : null, {
    enabled: Boolean(poll && patientId),
    immediate: false,
    onData: (fresh) => {
      if (Array.isArray(fresh)) state.setData(fresh);
    },
  });

  const bookings = useMemo(() => state.data || [], [state.data]);
  const buckets = useMemo(() => groupBookingsByBucket(bookings), [bookings]);

  return { ...state, bookings, buckets };
}

/**
 * Every booking for one doctor. `dateKey` narrows it to a single day, which is
 * what the daily queue and the manual-booking modal want.
 */
export function useDoctorBookings(doctorId, options = {}) {
  const { dateKey = null, poll = true, intervalMs = POLL_INTERVAL.DOCTOR } = options;

  const filters = useMemo(
    () => ({ doctorId, ...(dateKey ? { date: dateKey } : {}) }),
    [doctorId, dateKey],
  );

  const state = useAsync(() => bookingApi.list(filters), [doctorId, dateKey], {
    initialData: [],
    enabled: Boolean(doctorId),
  });

  usePoll(() => bookingApi.listFresh(filters), poll && doctorId ? intervalMs : null, {
    enabled: Boolean(poll && doctorId),
    immediate: false,
    onData: (fresh) => {
      if (Array.isArray(fresh)) state.setData(fresh);
    },
  });

  const bookings = useMemo(
    () => [...(state.data || [])].sort(compareBookingsChronologically),
    [state.data],
  );

  return { ...state, bookings };
}

/** Bookings for one clinic on one date — used to compute slot availability. */
export function useClinicDayBookings(clinicId, dateKey) {
  return useAsync(
    () => bookingApi.list({ clinicId, date: dateKey }),
    [clinicId, dateKey],
    { initialData: [], enabled: Boolean(clinicId && dateKey) },
  );
}

/**
 * Booking mutations, with optimistic local updates.
 *
 * `onChange` receives the updated list so the caller can keep its own copy in
 * step without waiting for the next poll.
 */
export function useBookingActions({ bookings = [], setBookings } = {}) {
  const [pendingId, setPendingId] = useState(null);

  const applyLocal = useCallback(
    (id, patch) => {
      if (!setBookings) return;
      setBookings(
        bookings.map((b) => (String(b.id) === String(id) ? { ...b, ...patch } : b)),
      );
    },
    [bookings, setBookings],
  );

  const create = useCallback(async (payload) => {
    const result = await bookingApi.create(payload);
    return result?.booking || result;
  }, []);

  const updateStatus = useCallback(
    async (id, status) => {
      setPendingId(id);
      try {
        const result = await bookingApi.updateStatus(id, status);
        applyLocal(id, { status });
        return result?.booking || result;
      } finally {
        setPendingId(null);
      }
    },
    [applyLocal],
  );

  const update = useCallback(
    async (id, patch) => {
      setPendingId(id);
      try {
        const result = await bookingApi.update(id, patch);
        applyLocal(id, patch);
        return result?.booking || result;
      } finally {
        setPendingId(null);
      }
    },
    [applyLocal],
  );

  const cancel = useCallback((id) => updateStatus(id, 'cancelled'), [updateStatus]);
  const confirm = useCallback((id) => updateStatus(id, 'confirmed'), [updateStatus]);
  const complete = useCallback((id) => updateStatus(id, 'completed'), [updateStatus]);

  /**
   * Move a booking to a different slot, keeping its locked price.
   *
   * `clinicId` is optional: the day modal shows one grid per clinic, so a drop
   * can land in a different clinic than the booking started in.
   */
  const reschedule = useCallback(
    (id, dateKey, time, clinicId) =>
      update(id, { date: dateKey, time, ...(clinicId ? { clinicId } : {}) }),
    [update],
  );

  const uploadReceipt = useCallback(
    async (id, depositReceipt) => {
      setPendingId(id);
      try {
        const result = await bookingApi.uploadReceipt(id, depositReceipt);
        applyLocal(id, { depositReceipt, depositPaid: true });
        return result?.booking || result;
      } finally {
        setPendingId(null);
      }
    },
    [applyLocal],
  );

  const confirmDeposit = useCallback(
    (id) => update(id, { depositConfirmed: true, status: 'confirmed', depositRejectionReason: null }),
    [update],
  );

  const rejectDeposit = useCallback(
    (id, reason) =>
      update(id, { depositConfirmed: false, depositPaid: false, depositRejectionReason: reason }),
    [update],
  );

  return {
    pendingId,
    create,
    update,
    updateStatus,
    cancel,
    confirm,
    complete,
    reschedule,
    uploadReceipt,
    confirmDeposit,
    rejectDeposit,
  };
}
